import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import type {
  PrincipalType,
  SetSalesAssignmentInput,
  CreateResourcePrivilegeInput,
  UpdateResourcePrivilegeInput,
  AddAssignmentInput,
  RemoveAssignmentInput,
  UpdateAssignmentInput,
  AddGrantInput,
  AddGrantsInput,
  RemoveGrantInput,
  UpdateGrantInput,
  AddScopeInput,
  RemoveScopeInput,
  UpdateScopeInput,
  ListResourcesInput,
  ListPrincipalsInput,
} from '@erp/rpc';

const QUERY_KEY_BASE = 'resource-privileges';

// ── Privileges ──

export function useListResourcePrivileges(input?: {
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'list', input],
    queryFn: () => rpcClient.resourcePrivileges.list(input ?? {}),
  });
}

export function useGetResourcePrivilege(id: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'get', id],
    queryFn: () => rpcClient.resourcePrivileges.get({ id: id as string }),
    enabled: !!id,
  });
}

export function useCreateResourcePrivilege() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateResourcePrivilegeInput) =>
      rpcClient.resourcePrivileges.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateResourcePrivilege() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateResourcePrivilegeInput) =>
      rpcClient.resourcePrivileges.update(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'get', vars.id] });
    },
  });
}

export function useDeleteResourcePrivilege() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpcClient.resourcePrivileges.delete({ id }),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: [QUERY_KEY_BASE, 'get', id] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

// ── Assignments ──

export function useAddAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddAssignmentInput) =>
      rpcClient.resourcePrivileges.addAssignment(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', vars.resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useRemoveAssignment(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveAssignmentInput) =>
      rpcClient.resourcePrivileges.removeAssignment(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateAssignment(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssignmentInput) =>
      rpcClient.resourcePrivileges.updateAssignment(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

// ── Grants ──

export function useAddGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddGrantInput) =>
      rpcClient.resourcePrivileges.addGrant(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', vars.resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useAddGrants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddGrantsInput) =>
      rpcClient.resourcePrivileges.addGrants(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', vars.resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useRemoveGrant(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveGrantInput) =>
      rpcClient.resourcePrivileges.removeGrant(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateGrant(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateGrantInput) =>
      rpcClient.resourcePrivileges.updateGrant(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

// ── Scopes (ABAC) ──

export function useAddScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddScopeInput) =>
      rpcClient.resourcePrivileges.addScope(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', vars.resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useRemoveScope(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveScopeInput) =>
      rpcClient.resourcePrivileges.removeScope(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

export function useUpdateScope(resourcePrivilegeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateScopeInput) =>
      rpcClient.resourcePrivileges.updateScope(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [QUERY_KEY_BASE, 'get', resourcePrivilegeId],
      });
      qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE, 'list'] });
    },
  });
}

// ── Lookups ──

export function useListResourceTypes() {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'resource-types'],
    queryFn: () => rpcClient.resourcePrivileges.listResourceTypes({}),
  });
}

export function useListResources(input: ListResourcesInput | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'resources', input],
    queryFn: () =>
      rpcClient.resourcePrivileges.listResources(input as ListResourcesInput),
    enabled: !!input,
  });
}

export function useListPrincipals(input: ListPrincipalsInput | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'principals', input],
    queryFn: () =>
      rpcClient.resourcePrivileges.listPrincipals(input as ListPrincipalsInput),
    enabled: !!input,
  });
}

// ── Sales assignments (Teams → Assignments tab) ──

export function useListSalesAssignments(
  customerId?: string | null,
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: [QUERY_KEY_BASE, 'sales-assignments', customerId ?? null],
    queryFn: () =>
      rpcClient.resourcePrivileges.listSalesAssignments(
        customerId ? { customerId } : {},
      ),
  });
}

export function useGetSalesAssignment(userId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'sales-assignments', 'get', userId],
    queryFn: () =>
      rpcClient.resourcePrivileges.getSalesAssignment({
        userId: userId as string,
      }),
    enabled: !!userId,
  });
}

export function useSetSalesAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetSalesAssignmentInput) =>
      rpcClient.resourcePrivileges.setSalesAssignment(input),
    onSuccess: () => {
      // Managed privileges surface on the admin page too — refresh everything.
      void qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
    },
  });
}

export function useRemoveSalesAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      rpcClient.resourcePrivileges.removeSalesAssignment({ userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
    },
  });
}

/**
 * Whether the current user's customer visibility is restricted by resource
 * privileges. Pages that surface prices (Quote Builder, Product Catalog) use
 * this to force a customer pick before loading price data.
 */
export function useMyRestrictions() {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'my-restrictions'],
    queryFn: () => rpcClient.resourcePrivileges.getMyRestrictions({}),
    staleTime: 60_000,
  });
}

export const PRINCIPAL_TYPE_LABELS: Record<PrincipalType, string> = {
  tenant: 'Tenant (everyone)',
  organization: 'Organization',
  user_group: 'Team',
  user: 'User',
};
